import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { sortByOptions } from 'constants/sortByOptions';
import {
  NLP_CAROUSEL_DESKTOP_TITLE,
  NLP_CAROUSEL_MOBILE_TITLE,
  NLP_CAROUSEL_VIEW_ALL_LINK,
  NLP_CAROUSEL_VIEW_ALL_TITLE,
} from 'constants/nlp-carousel';
import { useOnCancelAmends } from 'hooks/use-on-cancel-amends';

import { GA_GLP_RECOMMENDED_CAROUSEL_TYPE } from 'utils/clickContext/recommendedCarouselContext';

import BrowseHeader from 'components/BrowseHeader';
import EmptyCategory from 'components/ProductGrid/Empty/Category';
import ContentLocation from 'components/ContentLocation';
import NotFound from 'components/NotFound/NotFound';
import ProductGrid from 'components/ProductGrid';
import ProductsRefinements from 'components/Product/Refinements';
import Skeleton from 'components/Skeleton';
import RecommendedCarousel from 'components/RecommendedCarousel';
import { canShowRecommended } from 'components/RecommendedCarousel/canShowRecommended';
import { getFeatureFlags } from 'utils/feature-flags';
import {
  colourScallopGrey,
  colourCorporateGreen,
} from '@johnlewispartnership/wtr-ingredients/foundations/colours';
import BrowseMetaHeader from './MetaHeader';
import GroceriesLegalNotice from './GroceriesLegalNotice';

import styles from './GroceriesLandingPage.scss';

const browseDefaultPropRecommendations = [];

const browseDefaultPropLocation = {
  from: null,
  pathname: '',
  state: {
    data: {},
  },
};

const GroceriesLandingPage = ({
  category,
  categoryTitle,
  locationsLoading,
  productsLoading,
  location = browseDefaultPropLocation,
  fetchProducts,
  recommendations = browseDefaultPropRecommendations,
  hasSEOContent,
  isPageLoading,
  isPendingOrdersLoading,
}) => {
  useOnCancelAmends(() => {
    fetchProducts(category);
  });

  const categoryNotFound = category === null;

  const listDefinitions = useMemo(
    () => ({
      curation: 'Catalog',
      format: 'List',
      page: 'PLP',
      title: categoryTitle,
    }),
    [categoryTitle],
  );

  if (categoryNotFound) {
    return <NotFound />;
  }

  const headerLabel = {
    display: true,
    text: 'Browse by category',
  };

  const { glpNLPCarousel } = getFeatureFlags();

  const showRecommended = canShowRecommended(recommendations);

  const categoryLoading = category === undefined;

  const pageLoading =
    categoryLoading || locationsLoading || isPageLoading || isPendingOrdersLoading;

  const carouselListDefinitions = {
    curation: 'Personalised',
    format: 'Carousel',
    page: 'Groceries Homepage',
    title: '',
  };

  return (
    <Fragment>
      <BrowseMetaHeader location={location} />
      {pageLoading && (
        <Skeleton withSpinner spinnerPosition="top" height="100vh" data-testid="category-loading" />
      )}
      {!pageLoading && (
        <Fragment>
          <div className={styles.browseHeader}>
            <BrowseHeader linksLabel={headerLabel} location={location} />
            <ContentLocation name="header" preloadImages />
          </div>
          {showRecommended && (
            <div className={styles.recommendationsWrapper}>
              <div
                className={classNames('container-fluid', styles.removeMarginInMobile)}
                data-testid="rec-prod-carousel"
              >
                <RecommendedCarousel
                  products={recommendations}
                  titleDesktop={glpNLPCarousel ? NLP_CAROUSEL_DESKTOP_TITLE : 'You might like'}
                  titleMobileAndTablet={
                    glpNLPCarousel ? NLP_CAROUSEL_MOBILE_TITLE : 'You might like'
                  }
                  className={styles.chosenForYou}
                  typeForAnalytics={GA_GLP_RECOMMENDED_CAROUSEL_TYPE}
                  linkData={{
                    url: glpNLPCarousel ? NLP_CAROUSEL_VIEW_ALL_LINK : undefined,
                    text: glpNLPCarousel ? NLP_CAROUSEL_VIEW_ALL_TITLE : undefined,
                  }}
                  list="browse"
                  buttonsTheme={glpNLPCarousel ? 'light' : undefined}
                  backgroundColor={glpNLPCarousel ? colourCorporateGreen : undefined}
                  listDefinitions={carouselListDefinitions}
                />
              </div>
            </div>
          )}
          <ProductsRefinements hideCategoryFilter sortOptions={sortByOptions.browse} />
          {productsLoading ? (
            <Skeleton
              withSpinner
              spinnerPosition="top"
              height="100vh"
              backgroundColor={colourScallopGrey}
              data-testid="products-loading"
            />
          ) : (
            <ProductGrid Empty={EmptyCategory} listDefinitions={listDefinitions} />
          )}
          {hasSEOContent && (
            <div data-testid="seoContent" className={styles.seoContent}>
              <ContentLocation name="seoContent" />
            </div>
          )}
          <GroceriesLegalNotice />
        </Fragment>
      )}
    </Fragment>
  );
};

GroceriesLandingPage.displayName = 'Groceries Landing Page';

GroceriesLandingPage.propTypes = {
  recommendations: PropTypes.arrayOf(PropTypes.shape({})),
  fetchProducts: PropTypes.func.isRequired,
  category: PropTypes.shape({
    id: PropTypes.string,
  }),
  categoryTitle: PropTypes.string,
  locationsLoading: PropTypes.bool.isRequired,
  productsLoading: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    key: PropTypes.string,
    pathname: PropTypes.string,
    search: PropTypes.string,
    state: PropTypes.shape({}),
  }),
  hasSEOContent: PropTypes.bool.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  isPendingOrdersLoading: PropTypes.bool.isRequired,
};

export default GroceriesLandingPage;
